/**
 * Pure-data manifest of the interactive web-component blocks.
 *
 * Server-safe by construction: NO imports of the web-component classes (those
 * `extends HTMLElement` and cannot be evaluated on a server), so render paths
 * and the AI prompt builders can all import it. Single source of truth for:
 *   - preview/publish render (`usesInteractiveComponents` -> lazy-load runtime)
 *   - the copilot code-gen prompt (full emit contract)
 *   - the copilot orchestrator prompt (one-line routing summary)
 *
 * It lives in `@tripcart/shared` rather than beside the components because
 * operator-api builds the prompts and `@tripcart/page-builder` renders the
 * blocks — two packages that must not import each other, and a second copy is
 * exactly the drift this file exists to prevent.
 */

import { COMBOBOX_TYPE, COMPOUND_COMBOBOX_TYPE } from "./combobox-settings";
import { BLOG_POSTS_TYPE } from "./blog-posts";
import { RADIO_GROUP_TYPE } from "./radio-group-settings";
import { REVIEW_GRID_TYPE, REVIEW_ROW_TYPE } from "./reviews-block";
import { TOUR_ACCORDION_TYPE } from "./tour-accordion";
import { TOUR_CARDS_TYPE } from "./tour-cards";
import { TOUR_CAROUSEL_TYPE } from "./tour-carousel";

export interface InteractiveBlock {
  /** Custom element tag, e.g. "tc-tabs". */
  tag: string;
  /** Human label shown to the orchestrator. */
  label: string;
  /** One line: when the model should reach for this block. */
  whenToUse: string;
  /**
   * Canonical minimal markup the code-gen agent must emit. Deliberately omits
   * ids / aria-* / hidden / tabindex — the web component wires those at runtime
   * (self-heal), so the model cannot get them wrong.
   */
  example: string;
}

/**
 * Listing a block here makes the copilot ADVERTISE it, so an entry lands in
 * the same change as the runtime that renders it — never before. `tc-tabs`
 * ships with the `@tripcart/page-builder` web component (canvas + storefront
 * loader) and its authoring plugin.
 *
 * An `example` carries only the settings a model should reach for unprompted.
 * `tc-carousel` deliberately omits `loop` and `autoplay`: both are operator
 * trait toggles, autoplay carries a WCAG 2.2.2 obligation (it must render a
 * stop control), and whatever this example shows is what the model copies.
 */
export const INTERACTIVE_BLOCKS: InteractiveBlock[] = [
  {
    tag: "tc-tabs",
    label: "Tabs",
    whenToUse:
      "a set of labelled panels where only one shows at a time (tabbed sections, feature/plan comparisons, grouped FAQs).",
    example: [
      "<tc-tabs>",
      '  <div role="tablist">',
      '    <button role="tab"><span>Overview</span></button>',
      '    <button role="tab"><span>Pricing</span></button>',
      "  </div>",
      '  <div class="tc-tabs__panels">',
      '    <div role="tabpanel"><p>Overview content…</p></div>',
      '    <div role="tabpanel"><p>Pricing content…</p></div>',
      "  </div>",
      "</tc-tabs>",
    ].join("\n"),
  },
  {
    tag: "tc-accordion",
    label: "Accordion",
    whenToUse:
      'a list of titles that each open to show what sits under them, several at once by default (FAQs, itinerary days, policies). Each item is a native `<details>` holding a `<summary>` with the title heading, then one `div` with the item\'s content, which may hold any other blocks. Keep the classes exactly as shown. Add `open` to an item only when it should start open, and `several="false"` on the root only for one-open-at-a-time.',
    example: [
      '<tc-accordion class="tc-accordion">',
      '  <details class="tc-accordion__item">',
      '    <summary class="tc-accordion__trigger">',
      '      <h3 class="tc-accordion__title">What is included?</h3>',
      "    </summary>",
      '    <div class="tc-accordion__panel"><p>Answer…</p></div>',
      "  </details>",
      '  <details class="tc-accordion__item">',
      '    <summary class="tc-accordion__trigger">',
      '      <h3 class="tc-accordion__title">Can I cancel?</h3>',
      "    </summary>",
      '    <div class="tc-accordion__panel"><p>Answer…</p></div>',
      "  </details>",
      "</tc-accordion>",
    ].join("\n"),
  },
  {
    tag: "tc-carousel",
    label: "Carousel",
    whenToUse:
      'a row of slides the visitor scrolls through sideways (featured tours, photo galleries, testimonials). Not for a single hero image, and not for content that must be read in order. Every wrapper div carries a class and a `data-tc-part` name: keep both exactly as shown, because the block is styled by the class and run by the part name. Copy the nesting exactly: `track` holds the row still while the slides move, `list` is the strip that moves, each `slide` is one card, and `controls` with `buttons` inside it is what positions the arrows over the slides. Keep `role="list"` and `role="listitem"` — they are what tell a screen reader this is a list of cards. The two arrow buttons are optional — leave them out for a carousel without arrows — but copy them exactly when you include them, `disabled` included: the runtime enables them once it takes over, so a visitor never sees a button that does nothing. Give it MORE slides than `per-view` (3 when left out): with as many slides as fit in one view there is nothing to scroll, and it shows one dot and two dead arrows. Never put `data-tc-motion` on a slide or any other part of the block.',
    example: [
      '<tc-carousel class="tc-carousel-block" label="Featured tours" per-view="3" peek dots>',
      '  <div class="tc-carousel__track" data-tc-part="track">',
      '    <div class="tc-carousel__list" data-tc-part="list" role="list">',
      '      <div class="tc-carousel__slide" data-tc-part="slide" role="listitem"><h3>Desert sunrise</h3><p>Three days in the Sahara.</p></div>',
      '      <div class="tc-carousel__slide" data-tc-part="slide" role="listitem"><h3>Atlas trek</h3><p>Five days in the mountains.</p></div>',
      '      <div class="tc-carousel__slide" data-tc-part="slide" role="listitem"><h3>Coastal ride</h3><p>A week along the shore.</p></div>',
      "    </div>",
      "  </div>",
      '  <div class="tc-carousel__controls" data-tc-part="controls">',
      '    <div class="tc-carousel__buttons" data-tc-part="buttons">',
      '      <button type="button" class="tc-carousel__arrow" data-tc-part="arrow" data-tc-direction="prev" aria-label="Previous" disabled>',
      '        <tc-icon name="chevron-left"></tc-icon>',
      "      </button>",
      '      <button type="button" class="tc-carousel__arrow" data-tc-part="arrow" data-tc-direction="next" aria-label="Next" disabled>',
      '        <tc-icon name="chevron-right"></tc-icon>',
      "      </button>",
      "    </div>",
      "  </div>",
      "</tc-carousel>",
    ].join("\n"),
  },
  {
    tag: "tc-counter",
    label: "Counter",
    whenToUse:
      "one headline figure with a caption that says where it comes from (a trek's highest pass, days on the trail, a price per person). Use one counter per figure; put several in a row for a band of stats. Type the number as it should read, commas and decimals included — the runtime counts up to exactly that text. The prefix and suffix spans are optional; leave either out rather than emitting it empty. Every part carries its `data-tc-part` name, and the block does not lay out without them. `variant` is `plain` or `card`, `size` is `sm`, `md` or `lg`, `align` is `start`, `center` or `end`; `count-up` makes the number run up once when it scrolls into view.",
    example: [
      '<tc-counter variant="plain" size="md" align="start" count-up>',
      '  <div data-tc-part="figure">',
      '    <span data-tc-part="number">5,106</span>',
      '    <span data-tc-part="suffix">m</span>',
      "  </div>",
      '  <div data-tc-part="caption">Larkya La, the high point of the circuit</div>',
      "</tc-counter>",
    ].join("\n"),
  },
  {
    tag: "tc-dialog",
    label: "Dialog",
    whenToUse:
      'content that opens in a modal window over the page when the visitor presses a button (a booking enquiry, full terms, a map or a larger photo). Not for content every visitor needs to read, and never opens by itself. The trigger button stays in the page; the `<dialog>` holds a header with a title and a close button, then a body that takes any content. Every part carries its `data-tc-part` name. Leave out ids, `commandfor` and `command` — the block links the buttons to the dialog itself. `closedby="any"` lets a click on the backdrop close it; leave it out to close only by Esc and the close button.',
    example: [
      "<tc-dialog>",
      '  <button type="button" data-tc-part="trigger">Enquire now</button>',
      '  <dialog data-tc-part="dialog" closedby="any">',
      '    <div data-tc-part="header">',
      '      <h2 data-tc-part="title">Send us your dates</h2>',
      '      <button type="button" data-tc-part="close" aria-label="Close">',
      '        <tc-icon name="x"></tc-icon>',
      "      </button>",
      "    </div>",
      '    <div data-tc-part="body"><p>We reply within one working day.</p></div>',
      "  </dialog>",
      "</tc-dialog>",
    ].join("\n"),
  },
  {
    tag: "tc-lightbox",
    label: "Lightbox",
    whenToUse:
      "a set of photos that opens full screen over the page when the visitor presses a button, one photo at a time with previous/next arrows, a caption under each and a strip of thumbnails along the bottom (a tour's photo gallery, a hotel's rooms). Not for photos that should sit in the page — use a Carousel for that. It is a Dialog with a Carousel inside: the trigger button stays in the page, and the `<dialog>` holds a header (the gallery's title and the close button) and one `<tc-carousel>`. Copy the carousel's nesting, classes and part names exactly, as for a Carousel, keep every lightbox class as shown, and keep its settings as shown: one photo per view, no gap, loop on, `thumbs` on, `dots=\"false\"` (leaving `dots` out turns dots ON). The thumbnails and the photo counter are drawn by the block from the photos; never write them. Each slide holds one `<figure>` with an `<img>` and a `<figcaption>`. Give every `<img>` an `alt` that describes the photo; leave out `src` when you have no photo, and the operator fills the slot from the media library. Leave out ids, `commandfor` and `command` — the block links the buttons to the dialog itself.",
    example: [
      "<tc-lightbox>",
      '  <button type="button" data-tc-part="trigger">View photos</button>',
      '  <dialog class="tc-lightbox__window" data-tc-part="dialog" closedby="any" aria-label="Photo gallery">',
      '    <div class="tc-lightbox__header" data-tc-part="header">',
      '      <h2 class="tc-lightbox__title" data-tc-part="title">Photo gallery</h2>',
      '      <button type="button" class="tc-lightbox__close" data-tc-part="close" aria-label="Close">',
      '        <tc-icon name="x"></tc-icon>',
      "      </button>",
      "    </div>",
      '    <tc-carousel class="tc-carousel-block" label="Photo gallery" per-view="1" gap="none" dots="false" thumbs loop>',
      '      <div class="tc-carousel__track" data-tc-part="track">',
      '        <div class="tc-carousel__list" data-tc-part="list" role="list">',
      '          <div class="tc-carousel__slide" data-tc-part="slide" role="listitem">',
      '            <figure class="tc-lightbox__figure" data-tc-part="figure"><img alt="Camp at the foot of the dunes"><figcaption class="tc-lightbox__caption" data-tc-part="caption">Night one, Erg Chebbi</figcaption></figure>',
      "          </div>",
      '          <div class="tc-carousel__slide" data-tc-part="slide" role="listitem">',
      '            <figure class="tc-lightbox__figure" data-tc-part="figure"><img alt="Mules on the Tizi n\'Tichka pass"><figcaption class="tc-lightbox__caption" data-tc-part="caption">Crossing the High Atlas</figcaption></figure>',
      "          </div>",
      "        </div>",
      "      </div>",
      '      <div class="tc-carousel__controls" data-tc-part="controls">',
      '        <div class="tc-carousel__buttons" data-tc-part="buttons">',
      '          <button type="button" class="tc-carousel__arrow" data-tc-part="arrow" data-tc-direction="prev" aria-label="Previous photo" disabled>',
      '            <tc-icon name="chevron-left"></tc-icon>',
      "          </button>",
      '          <button type="button" class="tc-carousel__arrow" data-tc-part="arrow" data-tc-direction="next" aria-label="Next photo" disabled>',
      '            <tc-icon name="chevron-right"></tc-icon>',
      "          </button>",
      "        </div>",
      "      </div>",
      "    </tc-carousel>",
      "  </dialog>",
      "</tc-lightbox>",
    ].join("\n"),
  },
  {
    tag: "tc-gallery",
    label: "Gallery",
    whenToUse:
      'a set of photos shown in the page as a grid, where pressing any photo opens it full screen and the visitor can move through all of them (a tour\'s photos, a trip report). Use it instead of a Lightbox whenever the photos themselves should be visible on the page; use a Carousel for a row that slides sideways. Settings are attributes on `<tc-gallery>`: `layout` ("grid" crops every photo to `ratio`: "square", "4-3" or "16-9"; "masonry" keeps each photo\'s own height), `columns` (2, 3 or 4) and `gap` ("tight", "normal", "loose", or "custom" with `gap-size` in pixels up to 80). There are no captions. The grid is the only list of photos: each photo is a `<button>` holding one `<img>` and nothing else, and give every `<img>` an `alt` that describes it. Keep every class exactly as shown: the block is styled by them. After the grid comes a `<tc-lightbox>` with NO trigger button and an EMPTY slide list, exactly as shown — the block fills the list from the grid; never write slides into it. Leave out ids, `commandfor` and `command`.',
    example: [
      '<tc-gallery layout="grid" columns="3" ratio="square" gap="normal">',
      '  <div class="tc-gallery__grid" data-tc-part="grid">',
      '    <button type="button" class="tc-gallery__tile" data-tc-part="tile"><img class="tc-gallery__image" alt="Trekkers crossing the Larkya La pass" loading="lazy"></button>',
      '    <button type="button" class="tc-gallery__tile" data-tc-part="tile"><img class="tc-gallery__image" alt="Stone houses and fields at Samagaon" loading="lazy"></button>',
      '    <button type="button" class="tc-gallery__tile" data-tc-part="tile"><img class="tc-gallery__image" alt="A glacial lake on the route" loading="lazy"></button>',
      "  </div>",
      "  <tc-lightbox>",
      '    <dialog class="tc-lightbox__window" data-tc-part="dialog" closedby="any" aria-label="Photo gallery">',
      '      <div class="tc-lightbox__header" data-tc-part="header">',
      '        <h2 class="tc-lightbox__title" data-tc-part="title">Photo gallery</h2>',
      '        <button type="button" class="tc-lightbox__close" data-tc-part="close" aria-label="Close">',
      '          <tc-icon name="x"></tc-icon>',
      "        </button>",
      "      </div>",
      '      <tc-carousel class="tc-carousel-block" label="Photo gallery" per-view="1" gap="none" dots="false" loop>',
      '        <div class="tc-carousel__track" data-tc-part="track">',
      '          <div class="tc-carousel__list" data-tc-part="list" role="list"></div>',
      "        </div>",
      '        <div class="tc-carousel__controls" data-tc-part="controls">',
      '          <div class="tc-carousel__buttons" data-tc-part="buttons">',
      '            <button type="button" class="tc-carousel__arrow" data-tc-part="arrow" data-tc-direction="prev" aria-label="Previous photo" disabled>',
      '              <tc-icon name="chevron-left"></tc-icon>',
      "            </button>",
      '            <button type="button" class="tc-carousel__arrow" data-tc-part="arrow" data-tc-direction="next" aria-label="Next photo" disabled>',
      '              <tc-icon name="chevron-right"></tc-icon>',
      "            </button>",
      "          </div>",
      "        </div>",
      "      </tc-carousel>",
      "    </dialog>",
      "  </tc-lightbox>",
      "</tc-gallery>",
    ].join("\n"),
  },
];

/**
 * Interactive elements that are PARTS of a block rather than blocks of their
 * own. The Tour Search block stores a `<tc-combobox>` per picker, so the tag
 * must load the runtime, but it is no tile and the copilot is not offered it:
 * the copilot reads `INTERACTIVE_BLOCKS` alone. A compound root is listed by
 * its type too: its element is `<tc-combobox>`, but a saved one may keep only
 * the type (`COMPOUND_COMBOBOX_TYPE`). A `<tc-radio-group>` works with no
 * script, but only the runtime installs its sliding background.
 */
export const INTERACTIVE_PART_TAGS: string[] = [
  COMBOBOX_TYPE,
  COMPOUND_COMBOBOX_TYPE,
  RADIO_GROUP_TYPE,
];

/**
 * Data blocks whose stored node is not a custom element but whose RENDER is
 * one. A Reviews row is stored as `"type":"review-row"` (a Platform reviews
 * grid as `"type":"review-grid"`) and each of its cards
 * draws a `<tc-review-text>` for Read more (design.md D98), so that tag is
 * never in the saved document for the match below to find. (The block's Tabs
 * and Carousel are stored, so their tags are.) A Tours accordion's list is
 * stored as `"type":"tour-accordion"` and draws a `<tc-tour-accordion>`
 * (design.md D109). Every tour card, in the Tours grid, the Tours carousel
 * and the accordion, draws its destinations and its listed Tour info in a
 * `<tc-more-list>` (design.md D114), and so does a Blog posts card its
 * categories.
 */
export const INTERACTIVE_DATA_TYPES: string[] = [
  REVIEW_ROW_TYPE,
  REVIEW_GRID_TYPE,
  TOUR_ACCORDION_TYPE,
  TOUR_CARDS_TYPE,
  TOUR_CAROUSEL_TYPE,
  BLOG_POSTS_TYPE,
];

/**
 * A Video stays GrapesJS's own `"type":"video"`. Only a YouTube or Vimeo one
 * draws a `<tc-video>` click-to-play frame (design.md D112); an uploaded file
 * is a plain `<video>` that needs no runtime. The provider is always stored
 * for a hosted video, because GrapesJS's default is the file one (`so`).
 */
const HOSTED_VIDEO_MARKERS: string[] = ["yt", "ytnc", "vi"].map(
  (provider) => `"provider":"${provider}"`,
);

/** Every tag whose presence loads the runtime. */
export const INTERACTIVE_TAGS: string[] = [
  ...INTERACTIVE_BLOCKS.map((b) => b.tag),
  ...INTERACTIVE_PART_TAGS,
];

/**
 * True when serialized project data contains any interactive custom element,
 * so callers can lazily load the web-component runtime only when it is needed.
 *
 * Matches the stored `type` as well as `tagName` because GrapesJS omits a
 * `tagName` that equals its component type's default, so a saved node can
 * carry only one of the two. Missing it is silent and total: the runtime never
 * loads and the block renders as inert markup on a published page.
 *
 * Over-matching is safe — `defineInteractive` on a page with no such element
 * does nothing — so this errs toward loading.
 */
export function usesInteractiveComponents(projectData: unknown): boolean {
  if (!projectData) return false;
  const json =
    typeof projectData === "string" ? projectData : JSON.stringify(projectData);
  return (
    INTERACTIVE_TAGS.some(
      (tag) =>
        json.includes(`"tagName":"${tag}"`) || json.includes(`"type":"${tag}"`),
    ) ||
    INTERACTIVE_DATA_TYPES.some((type) => json.includes(`"type":"${type}"`)) ||
    HOSTED_VIDEO_MARKERS.some((marker) => json.includes(marker))
  );
}

/**
 * Short one-line-per-block summary for the orchestrator (routing and awareness
 * only — the full emit contract lives in the code-gen prompt). Empty string
 * when there are no blocks.
 */
export function describeInteractiveBlocks(): string {
  return INTERACTIVE_BLOCKS.map((b) => `- ${b.label}: ${b.whenToUse}`).join(
    "\n",
  );
}

/**
 * Holding interactive elements back until React has hydrated them.
 *
 * A block that streams in through its own Suspense boundary (the Departure
 * Dates block) lands in the document after the runtime has defined its
 * elements, so they would upgrade at once and write ids, ARIA state and
 * `popover` into markup React has not hydrated yet — a hydration mismatch,
 * and React redraws the whole block. An element inside `[data-tc-hold]`
 * starts only when its holder dispatches `tc-release`
 * (`tcBase` in `packages/page-builder/src/web-components/base.ts`).
 */
export const INTERACTIVE_HOLD_ATTR = "data-tc-hold";
export const INTERACTIVE_RELEASE_EVENT = "tc-release";
